import { useEffect, useRef } from "react";
import { fetchEventSource, type EventSourceMessage } from "@microsoft/fetch-event-source";
import { TOKEN_STORAGE_KEY } from "@/lib/constants";

export interface SseSubscriptionOptions {
  /** Full URL to the SSE endpoint. */
  url: string | null | undefined;
  /** Called for every server-sent event that has an event name. */
  onEvent: (event: EventSourceMessage) => void;
  /** Called once after the connection opens. Useful for one-shot refetch of the baseline state. */
  onOpen?: () => void;
  /** Extra dependencies that should force the effect to re-subscribe when they change. */
  deps?: unknown[];
}

/**
 * Subscribes to a Server-Sent Events endpoint with the current JWT as a Bearer header.
 *
 * Uses `@microsoft/fetch-event-source` instead of the native `EventSource`
 * because the latter can't set custom headers — we need the Authorization header.
 */
export function useSseSubscription({ url, onEvent, onOpen, deps = [] }: SseSubscriptionOptions) {
  const onEventRef = useRef(onEvent);
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onEventRef.current = onEvent;
    onOpenRef.current = onOpen;
  }, [onEvent, onOpen]);

  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();

    fetchEventSource(url, {
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_STORAGE_KEY) ?? ""}`,
      },
      openWhenHidden: false,
      async onopen(response) {
        if (response.ok && response.headers.get("content-type")?.includes("text/event-stream")) {
          onOpenRef.current?.();
          return;
        }
        if (response.status === 401 || response.status === 403) {
          controller.abort();
          throw new Error(`SSE auth failed: ${response.status}`);
        }
        throw new Error(`SSE bad response: ${response.status}`);
      },
      onmessage(msg) {
        if (!msg.event) return;
        onEventRef.current(msg);
      },
      onerror(err) {
        console.warn("[sse] connection error, will retry:", err);
      },
    }).catch((err) => {
      console.warn("[sse] terminated:", err);
    });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
}
